import React, { useRef, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Share2, Download, Copy, Check, Droplets, Heart, Zap } from 'lucide-react';
import { toast } from 'sonner';

// --- Canvas drawing hook ---
function useScoreCanvas({ score, hydration, gut, inflammation, streakCount, userName, today }) {
  const canvasRef = useRef(null);

  const drawCard = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const ctx = canvas.getContext('2d');
    const W = 600;
    const H = 400;
    canvas.width = W;
    canvas.height = H;

    // Background
    const grad = ctx.createLinearGradient(0, 0, W, H);
    grad.addColorStop(0, '#FF6B4A');
    grad.addColorStop(1, '#E8552F');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.roundRect(0, 0, W, H, 20);
    ctx.fill();

    // Subtle pattern
    ctx.globalAlpha = 0.05;
    ctx.fillStyle = '#FFFFFF';
    ctx.beginPath(); ctx.arc(480, 80, 120, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.arc(100, 350, 80, 0, Math.PI * 2); ctx.fill();
    ctx.globalAlpha = 1;

    // Header
    ctx.fillStyle = 'rgba(255,255,255,0.6)';
    ctx.font = '500 13px Manrope, sans-serif';
    ctx.fillText('INNERGLOW', 40, 45);
    ctx.fillStyle = 'rgba(255,255,255,0.5)';
    ctx.font = '400 12px Manrope, sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText(today, W - 40, 45);
    ctx.textAlign = 'left';

    // Score circle
    const cx = 120, cy = 180, r = 65;
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.fillStyle = 'rgba(163,177,138,0.25)'; ctx.fill();
    ctx.beginPath(); ctx.arc(cx, cy, r, -Math.PI / 2, -Math.PI / 2 + (score / 100) * Math.PI * 2);
    ctx.strokeStyle = '#4ADE80'; ctx.lineWidth = 8; ctx.lineCap = 'round'; ctx.stroke();
    ctx.fillStyle = '#FFFFFF'; ctx.font = 'bold 42px Manrope, sans-serif'; ctx.textAlign = 'center';
    ctx.fillText(score, cx, cy + 14);
    ctx.font = '400 12px Manrope, sans-serif'; ctx.fillStyle = 'rgba(255,255,255,0.6)';
    ctx.fillText('DETOX SCORE', cx, cy + 34); ctx.textAlign = 'left';

    // Sub-scores
    const drawSub = (x, y, label, val, color) => {
      ctx.fillStyle = color; ctx.beginPath(); ctx.arc(x, y, 5, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#FFFFFF'; ctx.font = 'bold 22px Manrope, sans-serif'; ctx.fillText(val, x + 16, y + 7);
      ctx.fillStyle = 'rgba(255,255,255,0.6)'; ctx.font = '400 12px Manrope, sans-serif'; ctx.fillText(label, x + 16, y + 24);
    };
    drawSub(250, 130, 'Hydration', hydration, '#4ADE80');
    drawSub(250, 190, 'Gut Health', gut, '#4ADE80');
    drawSub(250, 250, 'Inflammation', inflammation, '#A388EE');

    // Streak badge
    if (streakCount > 0) {
      ctx.fillStyle = 'rgba(194,115,89,0.3)'; ctx.beginPath(); ctx.roundRect(420, 120, 140, 50, 25); ctx.fill();
      ctx.fillStyle = '#FFFFFF'; ctx.font = 'bold 20px Manrope, sans-serif'; ctx.textAlign = 'center';
      ctx.fillText(`${streakCount}-day`, 490, 145);
      ctx.font = '400 11px Manrope, sans-serif'; ctx.fillStyle = 'rgba(255,255,255,0.7)';
      ctx.fillText('streak', 490, 160); ctx.textAlign = 'left';
    }

    // Footer
    if (userName) { ctx.fillStyle = 'rgba(255,255,255,0.4)'; ctx.font = '400 13px Manrope, sans-serif'; ctx.fillText(`@${userName.split(' ')[0].toLowerCase()}`, 40, H - 30); }
    ctx.fillStyle = 'rgba(255,255,255,0.3)'; ctx.font = '400 11px Manrope, sans-serif'; ctx.textAlign = 'right';
    ctx.fillText('Track your skin health at innerglow.app', W - 40, H - 30); ctx.textAlign = 'left';

    return canvas;
  }, [score, hydration, gut, inflammation, streakCount, userName, today]);

  return { canvasRef, drawCard };
}

// --- Score Preview (visual card) ---
const ScorePreview = ({ score, hydration, gut, inflammation, streakCount, today }) => (
  <div className="bg-gradient-to-br from-[#FF6B4A] to-[#E8552F] rounded-xl p-5 mb-4 text-white">
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs text-white/50 uppercase tracking-wider">InnerGlow</span>
      <span className="text-xs text-white/40">{today}</span>
    </div>
    <div className="flex items-center gap-6">
      <div className="text-center">
        <div className="w-16 h-16 rounded-full border-4 border-[#4ADE80] flex items-center justify-center">
          <span className="font-heading text-2xl font-bold">{score}</span>
        </div>
        <p className="text-[10px] text-white/50 mt-1">DETOX</p>
      </div>
      <div className="space-y-2 flex-1">
        <ScoreBar icon={<Droplets className="w-3.5 h-3.5 text-[#4ADE80]" />} value={hydration} color="#4ADE80" />
        <ScoreBar icon={<Heart className="w-3.5 h-3.5 text-[#4ADE80]" />} value={gut} color="#4ADE80" />
        <ScoreBar icon={<Zap className="w-3.5 h-3.5 text-[#A388EE]" />} value={inflammation} color="#A388EE" />
      </div>
    </div>
    {streakCount > 0 && (
      <div className="mt-3 text-center"><span className="text-xs bg-white/10 px-3 py-1 rounded-full">{streakCount}-day streak</span></div>
    )}
  </div>
);

const ScoreBar = ({ icon, value, color }) => (
  <div className="flex items-center gap-2">
    {icon}
    <div className="flex-1 h-1.5 bg-white/10 rounded-full"><div className="h-full rounded-full" style={{ width: `${value}%`, backgroundColor: color }} /></div>
    <span className="text-xs w-6 text-right">{value}</span>
  </div>
);

// --- Share Buttons ---
const ShareButtons = ({ onShare, onDownload, onCopy, onWhatsApp, copied }) => (
  <>
    <div className="grid grid-cols-3 gap-2">
      <Button onClick={onShare} className="bg-[#FF6B4A] hover:bg-[#E8552F] rounded-xl text-xs h-9" data-testid="share-native-btn">
        <Share2 className="w-3.5 h-3.5 mr-1" /> Share
      </Button>
      <Button onClick={onDownload} variant="outline" className="border-[#E5D0C9] rounded-xl text-xs h-9" data-testid="share-download-btn">
        <Download className="w-3.5 h-3.5 mr-1" /> Save
      </Button>
      <Button onClick={onCopy} variant="outline" className="border-[#E5D0C9] rounded-xl text-xs h-9" data-testid="share-copy-btn">
        {copied ? <Check className="w-3.5 h-3.5 mr-1 text-[#4ADE80]" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
        {copied ? 'Done' : 'Copy'}
      </Button>
    </div>
    <Button onClick={onWhatsApp} className="w-full mt-2 bg-[#25D366] hover:bg-[#1da851] rounded-xl text-xs h-9 text-white" data-testid="share-whatsapp-btn">
      WhatsApp
    </Button>
  </>
);

// --- Main Component ---
const ShareScoreCard = ({ detoxScore, userName, streak }) => {
  const [copied, setCopied] = useState(false);

  const score = detoxScore?.total_score || 0;
  const hydration = detoxScore?.hydration_score || 0;
  const gut = detoxScore?.gut_health_score || 0;
  const inflammation = detoxScore?.inflammation_score || 0;
  const today = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const streakCount = streak?.current_streak || 0;

  const { canvasRef, drawCard } = useScoreCanvas({ score, hydration, gut, inflammation, streakCount, userName, today });

  const shareText = `My Detox Score: ${score}/100\nHydration: ${hydration} | Gut Health: ${gut} | Inflammation: ${inflammation}${
    streakCount > 0 ? `\n${streakCount}-day streak!` : ''
  }\n\nTrack your skin health at innerglow.app`;

  const handleDownload = useCallback(() => {
    const canvas = drawCard();
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `detox-score-${today.replace(/[, ]+/g, '-')}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
    toast.success('Card downloaded! Share it on Instagram or WhatsApp.');
  }, [drawCard, today]);

  const handleShare = useCallback(async () => {
    const canvas = drawCard();
    if (!canvas) return;
    canvas.toBlob(async (blob) => {
      if (navigator.share && navigator.canShare) {
        try {
          const file = new File([blob], 'detox-score.png', { type: 'image/png' });
          await navigator.share({ title: `My Detox Score: ${score}/100`, text: shareText, files: [file] });
        } catch { handleDownload(); }
      } else { handleDownload(); }
    }, 'image/png');
  }, [drawCard, score, shareText, handleDownload]);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(shareText);
    setCopied(true);
    toast.success('Score copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  }, [shareText]);

  const handleWhatsApp = useCallback(() => {
    const text = encodeURIComponent(`My InnerGlow Detox Score: ${score}/100!\nHydration: ${hydration} | Gut: ${gut} | Inflammation: ${inflammation}${
      streakCount > 0 ? `\n${streakCount}-day streak!` : ''}\n\nTrack yours: innerglow.app`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  }, [score, hydration, gut, inflammation, streakCount]);

  return (
    <Card className="card-elevated border-0" data-testid="share-score-card">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-[#1A1514] text-base">
          <Share2 className="w-4 h-4 text-[#4ADE80]" /> Share Your Score
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ScorePreview score={score} hydration={hydration} gut={gut} inflammation={inflammation} streakCount={streakCount} today={today} />
        <ShareButtons onShare={handleShare} onDownload={handleDownload} onCopy={handleCopy} onWhatsApp={handleWhatsApp} copied={copied} />
        <canvas ref={canvasRef} className="hidden" />
      </CardContent>
    </Card>
  );
};

export default ShareScoreCard;
